// 텍스트 차이점을 계산하고 하이라이트하는 유틸리티
import React from 'react';

/**
 * 두 텍스트를 비교하여 변경된 부분을 식별합니다 (문자 단위 정확한 비교)
 * @param {string} original - 원본 텍스트
 * @param {string} revised - 수정된 텍스트
 * @returns {Array} 하이라이트 정보가 포함된 텍스트 구간들
 */
export const calculateTextDiff = (original, revised) => {
  console.log('[textDiff] 입력:', { original, revised });
  
  if (!original || !revised) {
    console.log('[textDiff] 텍스트 없음, 기본값 반환');
    return [{ text: revised || '', changed: false }];
  }
  
  // 동일한 텍스트인 경우
  if (original === revised) {
    console.log('[textDiff] 동일한 텍스트');
    return [{ text: revised, changed: false }];
  }

  try {
    // 문자 단위 LCS 기반 diff 계산
    const diffResult = calculateCharacterDiff(original, revised);
    console.log('[textDiff] 최종 결과:', diffResult);
    
    return diffResult;
  } catch (error) {
    console.error('[textDiff] 에러 발생, 단순 비교로 fallback:', error);
    return [{ text: revised, changed: true }];
  }
};

/**
 * 문자 단위 LCS 알고리즘을 사용한 정확한 diff 계산
 * @param {string} original - 원본 텍스트
 * @param {string} revised - 수정된 텍스트
 * @returns {Array} diff 결과
 */
const calculateCharacterDiff = (original, revised) => {
  console.log('[calculateCharacterDiff] 시작:', { originalLength: original.length, revisedLength: revised.length });
  
  // LCS 테이블 구성
  const lcsTable = buildLCSTable(original, revised);
  
  // LCS 테이블을 사용하여 diff 생성
  const diffResult = buildDiffFromLCS(original, revised, lcsTable);
  
  // 연속된 동일한 타입의 구간 병합
  const mergedResult = mergeConsecutiveDiffItems(diffResult);
  
  console.log('[calculateCharacterDiff] 결과:', mergedResult);
  return mergedResult;
};

/**
 * LCS(Longest Common Subsequence) 테이블 구성
 * @param {string} original - 원본 텍스트
 * @param {string} revised - 수정된 텍스트
 * @returns {Array} LCS 테이블
 */
const buildLCSTable = (original, revised) => {
  const m = original.length;
  const n = revised.length;
  
  // DP 테이블 초기화
  const lcs = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
  
  // LCS 테이블 채우기
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (original[i - 1] === revised[j - 1]) {
        lcs[i][j] = lcs[i - 1][j - 1] + 1;
      } else {
        lcs[i][j] = Math.max(lcs[i - 1][j], lcs[i][j - 1]);
      }
    }
  }
  
  return lcs;
};

/**
 * LCS 테이블을 사용하여 diff 결과 생성
 * @param {string} original - 원본 텍스트
 * @param {string} revised - 수정된 텍스트
 * @param {Array} lcsTable - LCS 테이블
 * @returns {Array} diff 결과
 */
const buildDiffFromLCS = (original, revised, lcsTable) => {
  const result = [];
  let i = original.length;
  let j = revised.length;
  
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && original[i - 1] === revised[j - 1]) {
      // 공통 문자
      result.unshift({ text: original[i - 1], changed: false });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || lcsTable[i][j - 1] >= lcsTable[i - 1][j])) {
      // 추가된 문자 (revised에만 있음)
      result.unshift({ text: revised[j - 1], changed: true });
      j--;
    } else if (i > 0) {
      // 삭제된 문자 (original에만 있음) - 수정된 텍스트에서는 표시하지 않음
      i--;
    }
  }
  
  return result;
};

/**
 * 연속된 동일한 타입의 diff 항목들을 병합
 * @param {Array} diffItems - diff 항목들
 * @returns {Array} 병합된 diff 항목들
 */
const mergeConsecutiveDiffItems = (diffItems) => {
  if (diffItems.length <= 1) return diffItems;
  
  const merged = [];
  let current = { ...diffItems[0] };
  
  for (let i = 1; i < diffItems.length; i++) {
    if (current.changed === diffItems[i].changed) {
      // 같은 타입이면 텍스트 합치기
      current.text += diffItems[i].text;
    } else {
      // 다른 타입이면 현재 그룹을 결과에 추가하고 새 그룹 시작
      merged.push(current);
      current = { ...diffItems[i] };
    }
  }
  
  // 마지막 그룹 추가
  merged.push(current);
  
  return merged;
};

/**
 * React 컴포넌트용 diff 렌더링
 * @param {Array} diffResult - calculateTextDiff의 결과
 * @returns {Array} React 엘리먼트들
 */
export const renderDiffAsReactElements = (diffResult) => {
  return diffResult.map((item, index) => {
    if (item.changed) {
      return (
        <span 
          key={index} 
          className="bg-yellow-200 rounded font-medium"
          title="수정된 부분"
        >
          {item.text}
        </span>
      );
    }
    return <span key={index}>{item.text}</span>;
  });
};

/**
 * diffResult에서 변경사항을 추출하여 수정 요약용 데이터로 변환
 * @param {Array} diffResult - calculateTextDiff의 결과
 * @param {string} original - 원본 텍스트
 * @param {string} revised - 수정된 텍스트
 * @returns {Array} 변경사항 배열 [{ original, revised, type }]
 */
export const extractChangesFromDiff = (diffResult, original, revised) => {
  console.log('[extractChangesFromDiff] 시작:', { diffResult, original, revised });
  
  if (!diffResult || !original || !revised) {
    console.log('[extractChangesFromDiff] 입력 데이터 부족');
    return [];
  }
  
  const changes = [];
  let originalIndex = 0;
  let revisedIndex = 0;
  
  // 변경사항을 찾기 위해 원본과 수정본을 직접 비교
  for (let i = 0; i < diffResult.length; i++) {
    const item = diffResult[i];
    
    if (item.changed) {
      // 변경된 부분의 경우, 원본에서 대응하는 부분 찾기
      const originalSegment = findCorrespondingOriginalSegment(
        original, 
        revised, 
        originalIndex, 
        revisedIndex, 
        item.text
      );
      
      if (originalSegment && originalSegment.text !== item.text) {
        const change = {
          original: originalSegment.text,
          revised: item.text,
          type: categorizeChange(originalSegment.text, item.text)
        };
        changes.push(change);
      }
      
      originalIndex = originalSegment ? originalSegment.endIndex : originalIndex;
      revisedIndex += item.text.length;
    } else {
      // 변경되지 않은 부분
      originalIndex += item.text.length;
      revisedIndex += item.text.length;
    }
  }
  
  // 공백 변경사항들을 그룹화
  const groupedChanges = groupWhitespaceChanges(changes);
  
  console.log('[extractChangesFromDiff] 추출된 변경사항:', groupedChanges);
  return groupedChanges;
};

/**
 * 변경사항의 종류를 분류하는 함수
 * @param {string} original - 원본 텍스트
 * @param {string} revised - 수정된 텍스트
 * @returns {string} 변경사항 종류 ('whitespace', 'content', 'mixed')
 */
const categorizeChange = (original, revised) => {
  const originalIsWhitespace = /^\s+$/.test(original);
  const revisedIsWhitespace = /^\s+$/.test(revised);
  const originalHasWhitespace = /\s/.test(original);
  const revisedHasWhitespace = /\s/.test(revised);
  
  // 둘 다 순수 공백인 경우
  if (originalIsWhitespace && revisedIsWhitespace) {
    return 'whitespace';
  }
  
  // 하나는 공백, 하나는 내용인 경우 (공백 추가/제거)
  if (originalIsWhitespace || revisedIsWhitespace) {
    return 'whitespace';
  }
  
  // 둘 다 내용이 있지만 공백이 포함된 경우 (복합 변경)
  if (originalHasWhitespace || revisedHasWhitespace) {
    return 'mixed';
  }
  
  // 순수 내용 변경
  return 'content';
};

/**
 * 공백 변경사항들을 그룹화하는 함수
 * @param {Array} changes - 변경사항 배열
 * @returns {Array} 그룹화된 변경사항 배열
 */
const groupWhitespaceChanges = (changes) => {
  const grouped = [];
  let whitespaceCount = 0;
  
  for (const change of changes) {
    if (change.type === 'whitespace') {
      whitespaceCount++;
    } else {
      // 이전에 누적된 공백 변경사항들을 추가
      if (whitespaceCount > 0) {
        grouped.push({
          original: '',
          revised: '',
          type: 'whitespace-group',
          count: whitespaceCount
        });
        whitespaceCount = 0;
      }
      
      // 현재 변경사항 추가
      grouped.push(change);
    }
  }
  
  // 마지막에 남은 공백 변경사항들 추가
  if (whitespaceCount > 0) {
    grouped.push({
      original: '',
      revised: '',
      type: 'whitespace-group',
      count: whitespaceCount
    });
  }
  
  return grouped;
};

/**
 * 변경된 부분에 대응하는 원본 구간을 찾는 함수
 * @param {string} original - 원본 텍스트
 * @param {string} revised - 수정된 텍스트
 * @param {number} originalIndex - 원본에서 현재 위치
 * @param {number} revisedIndex - 수정본에서 현재 위치
 * @param {string} changedText - 변경된 텍스트
 * @returns {Object} { text, endIndex }
 */
const findCorrespondingOriginalSegment = (original, revised, originalIndex, revisedIndex, changedText) => {
  // 다음 공통 부분을 찾아서 원본 구간 결정
  const nextCommonPart = findNextCommonSequence(original, revised, originalIndex, revisedIndex + changedText.length);
  
  if (nextCommonPart) {
    const originalSegment = original.substring(originalIndex, nextCommonPart.originalIndex);
    return {
      text: originalSegment,
      endIndex: nextCommonPart.originalIndex
    };
  }
  
  // 공통 부분을 찾지 못한 경우, 같은 길이만큼 추출
  const endIndex = Math.min(originalIndex + changedText.length, original.length);
  return {
    text: original.substring(originalIndex, endIndex),
    endIndex: endIndex
  };
};

/**
 * 다음 공통 문자열 시퀀스를 찾는 함수
 * @param {string} original - 원본 텍스트
 * @param {string} revised - 수정된 텍스트
 * @param {number} originalStart - 원본에서 시작 위치
 * @param {number} revisedStart - 수정본에서 시작 위치
 * @returns {Object|null} { originalIndex, revisedIndex } 또는 null
 */
const findNextCommonSequence = (original, revised, originalStart, revisedStart) => {
  const minLength = 3; // 최소 공통 길이
  
  for (let origI = originalStart; origI < original.length - minLength + 1; origI++) {
    for (let revI = revisedStart; revI < revised.length - minLength + 1; revI++) {
      let matchLength = 0;
      
      // 연속된 공통 문자열 길이 계산
      while (
        origI + matchLength < original.length &&
        revI + matchLength < revised.length &&
        original[origI + matchLength] === revised[revI + matchLength]
      ) {
        matchLength++;
      }
      
      if (matchLength >= minLength) {
        return {
          originalIndex: origI,
          revisedIndex: revI
        };
      }
    }
  }
  
  return null;
}; 